import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMyProducts, toggleSelected, clearSelection, bulkToggleActivation, toggleActivation, deleteProduct } from '../store/slices/productSlice';
import '../pages/ProductList.css';

// TODO: Extract filters/table components if complexity grows

const PAGE_SIZES = [10, 20, 50];

// Native select action handler added below inside ProductList

const ProductList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { products, isLoading, isError, message, selectedIds } = useSelector(state => state.products);

  // Local UI state (client-side filters & sorting for "my products")
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [sortField, setSortField] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  useEffect(() => {
    dispatch(getMyProducts());
  }, [dispatch]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [search, category, inStockOnly, sortField, sortDir, pageSize]);

  const categories = useMemo(() => {
    const set = new Set();
    products.forEach(p => { if (p.category) set.add(p.category); });
    return Array.from(set).sort();
  }, [products]);

  const filtered = useMemo(() => {
    let list = products.slice();
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => (p.title && p.title.toLowerCase().includes(q)) || (p.description && p.description.toLowerCase().includes(q)));
    }
    if (category) list = list.filter(p => p.category === category);
  if (inStockOnly) list = list.filter(p => (p.inventory && (p.inventory.available != null ? p.inventory.available > 0 : p.inventory.quantity > 0)));
    // Sort
    list.sort((a, b) => {
      const aVal = a[sortField];
      const bVal = b[sortField];
      if (aVal === bVal) return 0;
      if (sortDir === 'asc') return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });
    return list;
  }, [products, search, category, inStockOnly, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize) || 1;
  const pageSlice = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const allPageSelected = pageSlice.length > 0 && pageSlice.every(p => selectedIds.includes(p._id));
  const toggleSelectAllPage = () => {
    if (allPageSelected) {
      // remove page ids
      pageSlice.forEach(p => {
        if (selectedIds.includes(p._id)) dispatch(toggleSelected(p._id));
      });
    } else {
      pageSlice.forEach(p => {
        if (!selectedIds.includes(p._id)) dispatch(toggleSelected(p._id));
      });
    }
  };

  const handleSingleToggle = (id) => dispatch(toggleSelected(id));
  const clearAll = () => dispatch(clearSelection());

  const handleBulkActivation = () => {
    if (selectedIds.length === 0) return;
    dispatch(bulkToggleActivation({ ids: selectedIds }));
  };

  const handleSingleActivation = (id) => dispatch(toggleActivation(id));
  const handleEdit = (id) => navigate(`/products/${id}/edit`);
  const handleRowAction = (product, value) => {
    switch (value) {
      case 'toggle':
        handleSingleActivation(product._id);
        break;
      case 'edit':
        handleEdit(product._id);
        break;
      case 'delete':
        if (window.confirm('Delete this product? This can be reversed by admins only.')) {
          dispatch(deleteProduct(product._id));
        }
        break;
      default:
        break;
    }
  };

  return (
    <div className="product-list page-wrapper">
      <div className="page-header product-list-header">
        <div className="headings">
          <h1>Products</h1>
          <p>Manage your product catalog</p>
        </div>
        <div className="actions">
          <Link to="/products/create" className="btn primary">Create Product</Link>
        </div>
      </div>

      <div className="panel">
        <div className="filters-grid">
          <div className="field-group">
            <label htmlFor="prod-search">Search</label>
            <input id="prod-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search title or description" />
          </div>
          <div className="field-group">
            <label htmlFor="prod-category">Category</label>
            <select id="prod-category" value={category} onChange={e => setCategory(e.target.value)}>
              <option value="">All</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="field-group checkbox">
            <label>
              <input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} /> In stock only
            </label>
          </div>
          <div className="field-group">
            <label htmlFor="sort-field">Sort Field</label>
            <select id="sort-field" value={sortField} onChange={e => setSortField(e.target.value)}>
              <option value="createdAt">Created</option>
              <option value="title">Title</option>
              <option value="price">Base Price</option>
            </select>
          </div>
          <div className="field-group">
            <label htmlFor="sort-dir">Direction</label>
            <select id="sort-dir" value={sortDir} onChange={e => setSortDir(e.target.value)}>
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </select>
          </div>
          <div className="field-group">
            <label htmlFor="page-size">Page Size</label>
            <select id="page-size" value={pageSize} onChange={e => setPageSize(Number(e.target.value))}>
              {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="bulk-bar">
          <span>{selectedIds.length} selected</span>
          <div className="spacer" />
            <button type="button" className="btn" onClick={handleBulkActivation}>Toggle Activation</button>
            <button type="button" className="btn text" onClick={clearAll}>Clear</button>
        </div>
      )}

      <div className="table-wrapper panel">
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th><input type="checkbox" aria-label="Select all on page" checked={allPageSelected} onChange={toggleSelectAllPage} /></th>
                <th>Title</th>
                <th className="hide-sm">Category</th>
                <th>Price</th>
                <th className="hide-md">Inventory</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan="7" style={{ textAlign: 'center' }}>Loading...</td></tr>
              )}
              {!isLoading && pageSlice.length === 0 && (
                <tr><td colSpan="7" style={{ textAlign: 'center' }}>No products found</td></tr>
              )}
              {!isLoading && pageSlice.map(p => {
                const selected = selectedIds.includes(p._id);
                return (
                  <tr
                    key={p._id}
                    className={selected ? 'row-selected' : ''}
                    aria-label={`View details for ${p.title}`}
                    onClick={(e) => {
                      if (e.target.closest('button, input, a')) return; // ignore interactive
                      navigate(`/products/${p._id}`);
                    }}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        if (e.target.closest('button, input, a')) return;
                        navigate(`/products/${p._id}`);
                      }
                    }}
                  >
                    <td><input type="checkbox" checked={!!selected} onChange={() => handleSingleToggle(p._id)} aria-label={`Select product ${p.title}`} /></td>
                    <td>{p.title}</td>
                    <td className="hide-sm">{p.category || '-'}</td>
                    <td>{p.price?.base != null ? `$${p.price.base.toFixed(2)}` : '-'}</td>
                    <td className="hide-md">{p.inventory?.available != null ? p.inventory.available : (p.inventory?.quantity != null ? p.inventory.quantity : '-')}</td>
                    <td>
                      <span className={p.isActive ? 'status-pill active' : 'status-pill inactive'}>
                        {p.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <select
                        className="action-select"
                        aria-label={`Actions for ${p.title}`}
                        defaultValue=""
                        disabled={isLoading}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => { handleRowAction(p, e.target.value); e.target.value = ''; e.stopPropagation(); }}
                      >
                        <option value="" disabled>Actions…</option>
                        <option value="toggle">{p.isActive ? 'Deactivate' : 'Activate'}</option>
                        <option value="edit">Edit</option>
                        <option value="delete" className="danger">Delete</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="pagination-bar">
          <div className="pager-info">Page {page} of {totalPages}</div>
          <div className="pager-buttons">
            <button type="button" className="btn text" disabled={page === 1} onClick={() => setPage(p => p - 1)}>Prev</button>
            <button type="button" className="btn text" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>Next</button>
          </div>
        </div>
        {isError && <div className="error-inline">{message}</div>}
      </div>
    </div>
  );
};

export default ProductList;